import titleStyles from '@/lib/widgets/complex/title-block/TitleBlock.module.scss';

import {
  ConciergeCatalogItem,
  ConciergeItemCatalog,
  getConciergeItemAssetCollection,
  getConciergeItemGalleryImageNames,
  getConciergeItemGallerySrcs,
} from '@/lib/entities/concierge';
import { SeoEntries } from '@/lib/entities/seo/lib/seo-entries';

import { CallBack } from '@/lib/shared/blocks/CallBack';
import { Breadcrumbs } from '@/lib/shared/components/Breadcrumbs';
import { Button } from '@/lib/shared/components/Buttons';
import { RequestCallNew } from '@/lib/shared/components/Forms';
import { H1, P1, P2 } from '@/lib/shared/components/Typography';

import clsx from 'clsx';
import Image from 'next/image';

import { CatalogModalContent } from './CatalogModalContent';
import pageStyles from './ConciergeItemPage.module.scss';
import catalogStyles from './ConciergePages.module.scss';
import type { ConciergeItemPageProps } from './concierge-item-page-props';
import { openConciergeLeadModal } from './openConciergeLeadModal';

const bookLabel = {
  ru: 'Забронировать',
  en: 'Reserve',
} as const;

const seoBrand = {
  ru: 'VillaCarte Консьерж',
  en: 'VillaCarte Concierge',
} as const;

type ConciergeItemPageViewProps = {
  item: ConciergeCatalogItem;
  catalog: ConciergeItemCatalog;
  locale: 'ru' | 'en';
  breadcrumbs: ConciergeItemPageProps['breadcrumbs'];
  h1?: string | null;
};

export const ConciergeItemPageView = ({
  item,
  catalog,
  locale,
  breadcrumbs,
  h1,
}: ConciergeItemPageViewProps) => {
  const assetCollection = getConciergeItemAssetCollection(catalog);
  const gallerySrcs = getConciergeItemGallerySrcs(item, assetCollection);
  const heroImage = gallerySrcs[0] || '/images/placeholder_image.webp';
  const pageGalleryNames = getConciergeItemGalleryImageNames(item, {
    skipCover: true,
  });
  const sourceForm = `concierge_${assetCollection}_${item.slug}`;
  const handleCtaClick = () =>
    openConciergeLeadModal({ sourceForm, comment: item.title });

  return (
    <section className={pageStyles.page}>
      <div className="container">
        <Breadcrumbs
          items={breadcrumbs}
          className={catalogStyles.breadcrumbs}
        />
        <section className={clsx(titleStyles.titleBlock, pageStyles.heroBlock)}>
          <Image
            sizes="(max-width: 768px) 100vw, 1624px"
            src={heroImage}
            className={titleStyles.titleBlock_bg}
            alt={item.title}
            fill
            style={{ objectFit: 'cover', objectPosition: 'center' }}
            priority
            loader={({ src }) => src}
          />
          <div className={titleStyles.titleBlock_noise} />
          <div className={titleStyles.titleBlock_info}>
            <H1 className={titleStyles.titleBlock_title}>{h1 || item.title}</H1>
            <P1 className={titleStyles.titleBlock_price}>{item.price}</P1>
            <div className={titleStyles.titleBlock_location}>
              <P2>{item.subtitle || item.summary}</P2>
            </div>
            <div className={titleStyles.titleBlock_requestCallButton}>
              <Button
                as="button"
                type="button"
                label={item.modal?.ctaLabel || bookLabel[locale]}
                theme="light-invert"
                size="md"
                onClick={handleCtaClick}
              />
            </div>
          </div>
          <div className={titleStyles.titleBlock_requestCallWrapper}>
            <RequestCallNew
              theme="transparent"
              className={titleStyles.titleBlock_requestCallForm}
              title="requestACall"
              sourceForm={`${sourceForm}_header`}
              leadType="CONCIERGE"
              comment={item.title}
              eventName={['concierge']}
            />
          </div>
        </section>
      </div>
      <div className={clsx('container', pageStyles.body)}>
        <CatalogModalContent
          item={item}
          locale={locale}
          assetCollection={assetCollection}
          layout="page"
          galleryImageNames={pageGalleryNames}
          showGallery={pageGalleryNames.length > 0}
        />
      </div>
      <CallBack
        page="complex"
        buttonLabel="bookAConsultation"
        agreementText="byClickingBookAConsultation"
        leadType="CONCIERGE"
        comment={item.title}
        sourceForm={`${sourceForm}_bottom`}
        eventName={['concierge']}
      />
    </section>
  );
};

export const ConciergeItemPage = ({
  item,
  catalog,
  locale,
  pageSeo,
  path,
  breadcrumbs,
}: ConciergeItemPageProps) => {
  const gallery = getConciergeItemGallerySrcs(
    item,
    getConciergeItemAssetCollection(catalog)
  );

  return (
    <>
      <SeoEntries
        fallback={{
          title: `${item.title} | ${seoBrand[locale]}`,
          description: item.summary || item.subtitle,
          keywords: item.title,
          openGraphImage: gallery[0] || '/images/og.png',
        }}
        seo={pageSeo}
        language={locale}
        path={path}
      />
      <ConciergeItemPageView
        item={item}
        catalog={catalog}
        locale={locale}
        breadcrumbs={breadcrumbs}
        h1={pageSeo?.h1}
      />
    </>
  );
};
